import { useState, useEffect } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, collection, onSnapshot, updateDoc, addDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "./firebase";

import LandingPage, { TradeLoginPage } from "./pages/LandingPage";
import CustomerForm from "./pages/CustomerForm";
import BodyshopLogin from "./pages/BodyshopLogin";
import BodyshopPortal from "./pages/BodyshopPortal";
import AdminLogin from "./pages/AdminLogin";
import AdminPortal from "./pages/AdminPortal";
import LoadingScreen from "./components/LoadingScreen";

export default function App() {
  const [view, setView] = useState("landing");
  const [authUser, setAuthUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Live data
  const [leads, setLeads] = useState([]);
  const [shops, setShops] = useState([]);
  const [settings, setSettings] = useState({
    defaultPrice: 15, maxBuyers: 3,
    priceBodywork: 15, pricePartial: 18, priceFull: 25, priceHighBudget: 30,
    alertNewLead: true, autoClose30: true, requirePhotos: true, manualApproval: true,
    pixelId: ""
  });

  // Auth state listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setAuthUser(user);
        // Fetch user profile from Firestore
        const profileDoc = await getDoc(doc(db, "users", user.uid));
        if (profileDoc.exists()) {
          const profile = profileDoc.data();
          setUserProfile(profile);
          // Route based on role
          if (profile.role === "admin") setView("admin");
          else if (profile.role === "bodyshop") {
            setView(profile.status === "pending" ? "bodyshop-pending" : "bodyshop");
          }
        }
      } else {
        setAuthUser(null);
        setUserProfile(null);
      }
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  // Live leads listener (only for admin and active bodyshops)
  useEffect(() => {
    if (!authUser || !userProfile) return;
    const unsub = onSnapshot(collection(db, "leads"), (snap) => {
      setLeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [authUser, userProfile]);

  // Live shops listener (admin only)
  useEffect(() => {
    if (!authUser || userProfile?.role !== "admin") return;
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
      setShops(snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => u.role === "bodyshop"));
    });
    return () => unsub();
  }, [authUser, userProfile]);

  // Settings listener (all users need settings for pricing)
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "settings", "global"), (snap) => {
      if (snap.exists()) setSettings(s => ({...s, ...snap.data()}));
    });
    return () => unsub();
  }, []);

  // ── Data mutations ──────────────────────────────────────────────────────────
  const addLead = async (data) => {
    const tier = data.tier || "qualified";
    const tierPrice = tier === "bronze" ? (settings.bronzePrice || 8)
      : tier === "premium" ? (settings.premiumPrice || 25)
      : (settings.defaultPrice || 15);
    await addDoc(collection(db, "leads"), {
      ...data,
      createdAt: serverTimestamp(),
      status: "new",
      buyers: [],
      price: tierPrice,
      maxBuyers: settings.maxBuyers || 3,
    });
  };

  const updateLead = async (id, changes) => {
    await updateDoc(doc(db, "leads", id), changes);
  };

  const buyLead = async (leadId, shopId, leadPrice) => {
    const leadRef = doc(db, "leads", leadId);
    const leadSnap = await getDoc(leadRef);
    if (!leadSnap.exists()) return;
    const lead = leadSnap.data();
    const newBuyers = [...(lead.buyers || []), shopId];
    const newStatus = newBuyers.length >= lead.maxBuyers ? "closed" : lead.status;
    await updateDoc(leadRef, { buyers: newBuyers, status: newStatus });
    // Record purchase
    await addDoc(collection(db, "purchases"), {
      leadId, shopId, price: leadPrice,
      purchasedAt: serverTimestamp(),
    });
  };

  const updateShop = async (id, changes) => {
    await updateDoc(doc(db, "users", id), changes);
  };

  const updateSettings = async (newSettings) => {
    await setDoc(doc(db, "settings", "global"), newSettings);
    setSettings(newSettings);
  };

  const handleLogout = async () => {
    await signOut(auth);
    setView("landing");
    setUserProfile(null);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (authLoading) return <LoadingScreen />;

  if (view === "landing") return <LandingPage onStart={() => setView("form")} onTradeLogin={() => setView("trade")} />;
  if (view === "form") return <CustomerForm onSubmit={addLead} onBack={() => setView("landing")} />;
  if (view === "trade") return <TradeLoginPage onSelect={p => setView(p === "bodyshop" ? "bodyshop-login" : "admin-login")} onBack={() => setView("landing")} />;
  if (view === "bodyshop-login") return <BodyshopLogin onBack={() => setView("trade")} onPending={() => setView("bodyshop-pending")} onSuccess={() => setView("bodyshop")} />;
  if (view === "bodyshop-pending") return (
    <div style={{ minHeight:"100vh", background:"#080808", display:"flex", flexDirection:"column", justifyContent:"center", padding:"32px 20px", color:"#F5F5F5", fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ background:"#181818", border:"1px solid #252525", borderRadius:20, padding:"32px 24px", maxWidth:440, margin:"0 auto", textAlign:"center" }}>
        <div style={{ fontSize:52, marginBottom:16 }}>⏳</div>
        <h2 style={{ fontSize:22, fontWeight:900, marginBottom:8 }}>Application under review</h2>
        <p style={{ fontSize:14, color:"#888", marginBottom:20, lineHeight:1.7 }}>We're verifying your business. This usually takes 24–48 hours. We'll email you once approved.</p>
        <button onClick={handleLogout} style={{ width:"100%", padding:"13px", borderRadius:13, border:"1px solid #252525", background:"transparent", color:"#666", fontFamily:"inherit", fontSize:14, cursor:"pointer" }}>Sign out</button>
      </div>
    </div>
  );
  if (view === "bodyshop" && authUser) return <BodyshopPortal leads={leads} shopId={authUser.uid} userProfile={userProfile} onBuyLead={buyLead} onLogout={handleLogout} />;
  if (view === "admin-login") return <AdminLogin onBack={() => setView("trade")} onSuccess={() => setView("admin")} />;
  if (view === "admin" && authUser) return <AdminPortal leads={leads} shops={shops} settings={settings} onUpdateLead={updateLead} onUpdateShop={updateShop} onUpdateSettings={updateSettings} onLogout={handleLogout} />;

  return null;
}
